function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import { color, width, height, themeGet } from 'styled-system';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import { Box, CloseButton, deprecatedPropType, Flex } from 'pcln-design-system';

var OVERLAY_ANIMATION = function OVERLAY_ANIMATION(transitionState) {
  return "\n  opacity: 0;\n  transition: opacity .5s cubic-bezier(0.50, 0.00, 0.25, 1.00);\n  ".concat(transitionState === 'entering' ? "opacity: 0;" : '', "\n  ").concat(transitionState === 'entered' ? "opacity: 1;" : '', "\n  ").concat(transitionState === 'exiting' ? "opacity: 0;" : '', "\n  ").concat(transitionState === 'exited' ? "opacity: 0;" : '', "\n");
};

var DIALOG_ANIMATION = function DIALOG_ANIMATION(transitionState) {
  return "\n  transform: scale(0.5);\n  transition: transform .5s cubic-bezier(0.50, 0.00, 0.25, 1.00);\n  ".concat(transitionState === 'entering' ? "transform: scale(0.5);" : '', "\n  ").concat(transitionState === 'entered' ? "transform: scale(1);" : '', "\n  ").concat(transitionState === 'exiting' ? "transform: scale(0.5);" : '', "\n  ").concat(transitionState === 'exited' ? "transform: scale(0.5);" : '', "\n");
};

var getAnimation = function getAnimation(_ref) {
  var transitionstate = _ref.transitionstate,
      defaultAnimation = _ref.defaultAnimation,
      _ref$customAnimation = _ref.customAnimation,
      customAnimation = _ref$customAnimation === void 0 ? null : _ref$customAnimation;
  return typeof customAnimation === 'function' ? customAnimation(transitionstate) : defaultAnimation(transitionstate);
};

var Overlay = styled(DialogOverlay).withConfig({
  displayName: "Modal__Overlay",
  componentId: "sc-wyr0vx-0"
})(["background-color:rgba(0,0,0,0.7);bottom:0;left:0;overflow-y:auto;position:fixed;right:0;top:0;", " *{box-sizing:border-box;}", ";"], function (props) {
  var _props$theme$lineHeig, _props$theme$fontWeig;

  return "\n    z-index: ".concat(props.zindex || 100, ";\n    font-family: ").concat(props.theme.font, ";\n    line-height: ").concat((_props$theme$lineHeig = props.theme.lineHeights) === null || _props$theme$lineHeig === void 0 ? void 0 : _props$theme$lineHeig.standard, ";\n    font-weight: ").concat((_props$theme$fontWeig = props.theme.fontWeights) === null || _props$theme$fontWeig === void 0 ? void 0 : _props$theme$fontWeig.medium, ";\n  ");
}, getAnimation);
var Dialog = styled(DialogContent).withConfig({
  displayName: "Modal__Dialog",
  componentId: "sc-wyr0vx-1"
})(["", " ", " ", " position:relative;margin-left:auto;margin-right:auto;box-shadow:", ";border-radius:", ";&:focus{outline:none;}", " ", " ", ""], color, width, height, function (props) {
  return props.theme.boxShadows[3];
}, themeGet('borderRadii.xl'), function (props) {
  return !props.fullScreen ? "\n    max-height: 100vh;\n    max-width: calc(100vw - 32px);\n  " : "\n    width: 100vw;\n    height: 100vh;\n  ";
}, function (props) {
  return props.enableoverflow && "\n    max-height: none;\n  ";
}, getAnimation);
var HeaderWrapper = styled.div.withConfig({
  displayName: "Modal__HeaderWrapper",
  componentId: "sc-wyr0vx-2"
})(["flex-shrink:0;"]);
var FloatCloseButton = styled(CloseButton).withConfig({
  displayName: "Modal__FloatCloseButton",
  componentId: "sc-wyr0vx-3"
})(["position:absolute;top:16px;right:16px;z-index:2;svg{vertical-align:top;}&:focus{background-color:", ";outline:none;}"], function (props) {
  return props.theme.colors.borderGray;
});
var ContentWrapper = styled(Box).withConfig({
  displayName: "Modal__ContentWrapper",
  componentId: "sc-wyr0vx-4"
})(["position:relative;", ""], function (props) {
  if (!props.enableoverflow) {
    return "\n        overflow-y: auto;\n        overflow-x: hidden;\n        height: 100%;\n        -webkit-overflow-scrolling: touch;\n      ";
  }
});
var OverlayWrapper = styled.div.withConfig({
  displayName: "Modal__OverlayWrapper",
  componentId: "sc-wyr0vx-5"
})(["display:table;height:100%;width:100%;"]);
var DialogWrapper = styled(Box).withConfig({
  displayName: "Modal__DialogWrapper",
  componentId: "sc-wyr0vx-6"
})(["display:table-cell;vertical-align:", ";position:relative;height:100%;", ";"], function (_ref2) {
  var verticalAlignment = _ref2.verticalAlignment;
  return verticalAlignment;
}, function (props) {
  return props.enableoverflow && "\n    padding-top: 24px;\n    padding-bottom: 24px;\n  ";
});
var DialogInnerWrapper = styled(Flex).withConfig({
  displayName: "Modal__DialogInnerWrapper",
  componentId: "sc-wyr0vx-7"
})(["position:relative;height:100%;"]);

var Modal = function Modal(_ref3) {
  var ariaLabel = _ref3.ariaLabel,
      ariaLabelledBy = _ref3.ariaLabelledBy,
      bg = _ref3.bg,
      children = _ref3.children,
      className = _ref3.className,
      dialogAnimation = _ref3.dialogAnimation,
      disableCloseButton = _ref3.disableCloseButton,
      enableOverflow = _ref3.enableOverflow,
      fullScreen = _ref3.fullScreen,
      header = _ref3.header,
      height = _ref3.height,
      imgMode = _ref3.imgMode,
      isOpen = _ref3.isOpen,
      onClose = _ref3.onClose,
      overlayAnimation = _ref3.overlayAnimation,
      timeout = _ref3.timeout,
      verticalAlignment = _ref3.verticalAlignment,
      width = _ref3.width,
      zIndex = _ref3.zIndex;
  var dialogHeight = height;

  if (enableOverflow) {
    dialogHeight = null;
  } else if (fullScreen) {
    dialogHeight = [1];
  }

  return /*#__PURE__*/React.createElement(Transition, {
    "in": isOpen,
    unmountOnExit: true,
    timeout: timeout
  }, function (state) {
    return /*#__PURE__*/React.createElement(Overlay, {
      onDismiss: onClose,
      zindex: zIndex,
      transitionstate: state,
      initialFocusRef: null,
      defaultAnimation: OVERLAY_ANIMATION,
      customAnimation: overlayAnimation
    }, /*#__PURE__*/React.createElement(OverlayWrapper, null, /*#__PURE__*/React.createElement(DialogWrapper, {
      enableoverflow: enableOverflow,
      verticalAlignment: verticalAlignment
    }, /*#__PURE__*/React.createElement(Dialog, {
      width: fullScreen ? 1 : width,
      bg: bg,
      height: dialogHeight,
      transitionstate: state,
      className: className,
      enableoverflow: enableOverflow ? 'true' : null,
      defaultAnimation: DIALOG_ANIMATION,
      customAnimation: dialogAnimation,
      fullScreen: fullScreen,
      "data-testid": "dialog-content",
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy
    }, /*#__PURE__*/React.createElement(DialogInnerWrapper, {
      flexDirection: "column"
    }, header && /*#__PURE__*/React.createElement(HeaderWrapper, null, header), /*#__PURE__*/React.createElement(ContentWrapper, {
      borderRadius: "xl",
      p: imgMode ? 0 : 16,
      header: header,
      enableoverflow: enableOverflow
    }, enableOverflow && !disableCloseButton && /*#__PURE__*/React.createElement(FloatCloseButton, {
      "data-testid": "pcln-modal-close",
      header: header,
      onClick: onClose
    }), children))))));
  });
};

Modal.defaultProps = {
  bg: 'white',
  dialogAnimation: null,
  disableCloseButton: false,
  enableOverflow: false,
  header: null,
  height: 420,
  isOpen: false,
  overlayAnimation: null,
  timeout: 500,
  verticalAlignment: 'middle'
};

var validateAriaProps = function validateAriaProps(props, propName, componentName) {
  if (!props.ariaLabel && !props.ariaLabelledBy) {
    return new Error("A <".concat(componentName, "> must have either an 'ariaLabel' or 'ariaLabelledBy' prop."));
  }
};

Modal.propTypes = _objectSpread(_objectSpread(_objectSpread({}, width.propTypes), height.propTypes), {}, {
  ariaLabel: validateAriaProps,
  ariaLabelledBy: validateAriaProps,
  bg: deprecatedPropType('color'),
  className: PropTypes.string,
  dialogAnimation: PropTypes.func,
  disableCloseButton: PropTypes.bool,
  enableOverflow: PropTypes.bool,
  fullScreen: PropTypes.bool,
  header: PropTypes.any,
  headerBg: PropTypes.string,
  imgMode: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  overlayAnimation: PropTypes.func,
  timeout: PropTypes.number,
  title: PropTypes.string,
  verticalAlignment: PropTypes.string,
  zIndex: PropTypes.number
});
export default Modal;